// src/pages/admin/AdminDashboard.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { publicHolidays } from "../../data/publicHolidays";
import { Card, ListGroup, Badge, Row, Col, Modal, Button } from "react-bootstrap";
import StatsCard from "../../components/StatsCard";
import SalaryCard from "../../components/SalaryCard";
import AdminChart from "./AdminChart";
import { getStudents } from "../../services/userService";
import { getTasks } from "../../services/taskService";
import { getRelevantActivities } from "../../services/activityService";
import { getMaterials } from "../../services/materialService";
import { getSalaryStatistics } from "../../services/salaryService";
import { getCollegeStatistics } from "../../services/collegeService";
import { getRevenueStatistics } from "../../services/revenueService";
import Loader from "../../components/Loader";
import CleanupManagement from "../../components/CleanupManagement";
import { formatDistanceToNow } from "date-fns";
import { Clock, User, CheckCircle, AlertCircle, FileText, LogIn, LogOut } from "lucide-react";

import { useAuth } from "../../context/AuthProvider";

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Public Holidays logic
  const [upcomingHoliday, setUpcomingHoliday] = useState(null);
  const [todayHoliday, setTodayHoliday] = useState(null);
  const [allHolidays, setAllHolidays] = useState([]);
  const [showHolidayModal, setShowHolidayModal] = useState(false);

  useEffect(() => {
    // Filter holidays for current year
    const year = new Date().getFullYear();
    const holidays = publicHolidays.filter(h => h.date.startsWith(year.toString()));
    setAllHolidays(holidays);

    // Show info message one day before a holiday and on the holiday
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().slice(0, 10);
    const foundTomorrow = holidays.find(h => h.date === tomorrowStr);
    const foundToday = holidays.find(h => h.date === todayStr);
    setUpcomingHoliday(foundTomorrow || null);
    setTodayHoliday(foundToday || null);
  }, []);

  const [stats, setStats] = useState([]);
  const [salaryStats, setSalaryStats] = useState(null);
  const [collegeStats, setCollegeStats] = useState(null);
  const [revenueStats, setRevenueStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salaryLoading, setSalaryLoading] = useState(true);
  const [collegeLoading, setCollegeLoading] = useState(true);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);

  useEffect(() => {
    const fetchAdminStats = async () => {
      setLoading(true);
      const studentsList = await getStudents();
      const tasksList = await getTasks();
      const materialsList = await getMaterials();

      setStats([
        {
          title: "Registered Students",
          value: studentsList.length,
          color: "bg-indigo-500",
          navigationPath: "/students"
        },
        {
          title: "Tasks Assigned",
          value: tasksList.length,
          color: "bg-pink-500",
          navigationPath: "/tasks"
        },
        {
          title: "Completed Tasks",
          value: tasksList.filter((task) => task.status === "completed").length,
          color: "bg-green-500",
          navigationPath: "/tasks"
        },
        {
          title: "Pending Tasks",
          value: tasksList.filter((task) => task.status === "pending").length,
          color: "bg-yellow-500",
          navigationPath: "/tasks"
        },
        {
          title: "Materials",
          value: materialsList.length,
          color: "bg-purple-500",
          navigationPath: "/materials"
        },
      ]);

      setLoading(false);
    };

    const fetchSalaryStats = async () => {
      setSalaryLoading(true);
      try {
        const currentMonth = new Date().getMonth() + 1;
        const currentYear = new Date().getFullYear();
        const stats = await getSalaryStatistics({ month: currentMonth, year: currentYear });
        setSalaryStats(stats);
      } catch (error) {
        console.error("Error fetching salary statistics:", error);
        setSalaryStats(null);
      }
      setSalaryLoading(false);
    };

    const fetchCollegeStats = async () => {
      setCollegeLoading(true);
      try {
        const stats = await getCollegeStatistics();
        setCollegeStats(stats);
      } catch (error) {
        console.error("Error fetching college statistics:", error);
        setCollegeStats(null);
      }
      setCollegeLoading(false);
    };

    const fetchRevenueStats = async () => {
      setRevenueLoading(true);
      try {
        const stats = await getRevenueStatistics();
        setRevenueStats(stats);
      } catch (error) {
        console.error("Error fetching revenue statistics:", error);
        setRevenueStats(null);
      }
      setRevenueLoading(false);
    };

    const fetchRecentActivities = async () => {
      setActivitiesLoading(true);
      try {
        const activities = await getRelevantActivities(user?.id, "admin", 7);
        setRecentActivities(activities);
      } catch (error) {
        console.error("Error fetching recent activities:", error);
        setRecentActivities([]);
      }
      setActivitiesLoading(false);
    };

    fetchAdminStats();
    fetchSalaryStats();
    fetchCollegeStats();
    fetchRevenueStats();
    fetchRecentActivities();
  }, []);

  const getActivityIcon = (type) => {
    switch (type) {
      case "task_completed":
        return <CheckCircle size={16} className="text-green-500" />;
      case "task_assigned":
        return <FileText size={16} className="text-blue-500" />;
      case "login":
        return <User size={16} className="text-indigo-500" />;
      case "profile_updated":
        return <User size={16} className="text-orange-500" />;
      case "check_in":
        return <LogIn size={16} className="text-success" />;
      case "check_out":
        return <LogOut size={16} className="text-danger" />;
      default:
        return <AlertCircle size={16} className="text-gray-500" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "student":
        return "primary";
      case "tutor":
        return "warning";
      default:
        return "secondary";
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Admin Dashboard</h2>

      {/* Public Holidays Info Banner and Button Side by Side */}
      {(todayHoliday || upcomingHoliday) && (
        <div className="d-flex align-items-center mb-3" style={{gap: '1rem', flexWrap: 'nowrap'}}>
          {todayHoliday && (
            <div className="alert alert-success mb-0 flex-grow-1 d-flex align-items-center" style={{minHeight: '48px'}}>
              <span><strong>Today is a public holiday:</strong> <b>{todayHoliday.name}</b> ({todayHoliday.date}). Enjoy your holiday!</span>
            </div>
          )}
          {!todayHoliday && upcomingHoliday && (
            <div className="alert alert-info mb-0 flex-grow-1 d-flex align-items-center" style={{minHeight: '48px'}}>
              <span><strong>Info:</strong> Tomorrow is <b>{upcomingHoliday.name}</b> ({upcomingHoliday.date}). Enjoy your holiday!</span>
            </div>
          )}
          <Button
            variant="outline-info"
            size="sm"
            className="ms-auto"
            style={{minWidth: '170px', whiteSpace: 'nowrap'}}
            onClick={() => navigate("/holidays")}
          >
            View Public Holidays
          </Button>
        </div>
      )}

      {loading && <Loader />}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {stats.map((stat) => (
          <StatsCard key={stat.title} {...stat} />
        ))}
      </div>

      {/* Salary Statistics Section */}
      <Card className="shadow mt-3 mb-4">
        <Card.Body>
          <Card.Title className="d-flex align-items-center gap-2 mb-3">
            💰 Salary Overview - Current Month
          </Card.Title>
          {salaryLoading ? (
            <div className="text-center py-3">
              <Loader />
            </div>
          ) : salaryStats ? (
            <Row>
              <Col md={3}>
                <SalaryCard
                  title="Total Records"
                  value={salaryStats.totalRecords}
                  variant="primary"
                  icon="file-alt"
                  navigationPath="/salary"
                />
              </Col>
              <Col md={3}>
                <SalaryCard
                  title="Total Paid"
                  value={salaryStats.totalPaid}
                  variant="success"
                  icon="check-circle"
                  navigationPath="/salary"
                />
              </Col>
              <Col md={3}>
                <SalaryCard
                  title="Total Pending"
                  value={salaryStats.totalPending}
                  variant="warning"
                  icon="clock"
                  navigationPath="/salary"
                />
              </Col>
              <Col md={3}>
                <SalaryCard
                  title="Avg Net Salary"
                  value={salaryStats.averageNetSalary}
                  variant="info"
                  icon="chart-bar"
                  navigationPath="/salary"
                />
              </Col>
            </Row>
          ) : (
            <div className="text-center text-muted py-3">
              No salary data available for current month
            </div>
          )}
        </Card.Body>
      </Card>

      {/* College Statistics Section */}
      <Card className="shadow mt-3 mb-4">
        <Card.Body>
          <Card.Title className="d-flex align-items-center gap-2 mb-3">
            🏫 College Partnerships Overview
          </Card.Title>
          {collegeLoading ? (
            <div className="text-center py-3">
              <Loader />
            </div>
          ) : collegeStats ? (
            <Row>
              <Col md={3}>
                <SalaryCard
                  title="Total Colleges"
                  value={collegeStats.totalColleges}
                  variant="primary"
                  icon="building"
                  navigationPath="/colleges"
                />
              </Col>
              <Col md={3}>
                <SalaryCard
                  title="Active Partnerships"
                  value={collegeStats.activeColleges}
                  variant="success"
                  icon="check-circle"
                  navigationPath="/colleges"
                />
              </Col>
              <Col md={3}>
                <SalaryCard
                  title="Students Assigned"
                  value={collegeStats.totalStudentsAssigned}
                  variant="info"
                  icon="users"
                  navigationPath="/colleges"
                />
              </Col>
              <Col md={3}>
                <SalaryCard
                  title="Avg Students/College"
                  value={Math.round(collegeStats.averageStudentsPerCollege)}
                  variant="warning"
                  icon="chart-bar"
                  navigationPath="/colleges"
                />
              </Col>
            </Row>
          ) : (
            <div className="text-center text-muted py-3">
              No college data available
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Revenue Statistics Section */}
      <Card className="shadow mt-3 mb-4">
        <Card.Body>
          <Card.Title className="d-flex align-items-center gap-2 mb-3">
            📊 Revenue Overview - Current Month
          </Card.Title>
          {revenueLoading ? (
            <div className="text-center py-3">
              <Loader />
            </div>
          ) : revenueStats ? (
            <Row>
              <Col md={3}>
                <SalaryCard
                  title="Total Income"
                  value={new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    minimumFractionDigits: 0,
                  }).format(revenueStats.totalIncome)}
                  variant="success"
                  icon="trending-up"
                  navigationPath="/revenue"
                />
              </Col>
              <Col md={3}>
                <SalaryCard
                  title="Total Expenses"
                  value={new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    minimumFractionDigits: 0,
                  }).format(revenueStats.totalExpenses)}
                  variant="danger"
                  icon="trending-down"
                  navigationPath="/revenue"
                />
              </Col>
              <Col md={3}>
                <SalaryCard
                  title="Net Profit"
                  value={new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    minimumFractionDigits: 0,
                  }).format(revenueStats.netProfit)}
                  variant={revenueStats.netProfit >= 0 ? "primary" : "warning"}
                  icon="dollar-sign"
                  navigationPath="/revenue"
                />
              </Col>
              <Col md={3}>
                <SalaryCard
                  title="Profit Margin"
                  value={`${(revenueStats.profitMargin || 0).toFixed(1)}%`}
                  variant="info"
                  icon="pie-chart"
                  navigationPath="/revenue"
                />
              </Col>
            </Row>
          ) : (
            <div className="text-center text-muted py-3">
              No revenue data available for current month
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Recent User Activities Section */}
      <Card className="shadow mt-3 mb-4">
        <Card.Body>
          <Card.Title className="d-flex align-items-center gap-2">
            <Clock size={20} className="text-primary" />
            Recent User Activities (Last 7)
          </Card.Title>
          {activitiesLoading ? (
            <div className="text-center py-3">
              <Loader />
            </div>
          ) : recentActivities.length === 0 ? (
            <p className="text-center text-muted py-3">
              No recent activities found.
            </p>
          ) : (
            <ListGroup variant="flush">
              {recentActivities.map((activity, index) => (
                <ListGroup.Item key={activity.id} className="border-0 py-3">
                  <div className="d-flex align-items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <div className="d-flex align-items-center gap-2">
                          <strong className="text-sm">
                            {activity.userName}
                          </strong>
                          <Badge
                            bg={getRoleBadgeColor(activity.userRole)}
                            className="text-xs"
                          >
                            {activity.userRole}
                          </Badge>
                          {activity.userLevel && (
                            <Badge
                              bg="secondary"
                              className="text-xs"
                            >
                              Level {activity.userLevel}
                            </Badge>
                          )}
                        </div>
                        <small className="text-muted">
                          {activity.createdAt ? (
                            formatDistanceToNow(new Date(activity.createdAt), {
                              addSuffix: true,
                            })
                          ) : (
                            <span className="text-warning">Just now</span>
                          )}
                        </small>
                      </div>
                      <p className="mb-0 text-muted small">{activity.text}</p>
                    </div>
                  </div>
                  {index < recentActivities.length - 1 && (
                    <hr className="mt-3 mb-0 opacity-25" />
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Card.Body>
      </Card>

      {/* Cleanup Management Section */}
      <div className="mt-4">
        <CleanupManagement />
      </div>
    </div>
  );
}
